import { useEffect, useState, useCallback } from "react";
import { api, mediaUrl } from "@/lib/api";
import { toast } from "sonner";
import { GitMerge, X, ArrowRight, ShieldAlert } from "lucide-react";

function AttrRow({ label, a, b }) {
  const diff = (a || "").toLowerCase() !== (b || "").toLowerCase();
  return (
    <div className="grid grid-cols-2 gap-4 py-2 border-t border-white/5 text-sm">
      <div className={diff ? "text-amber-400" : "text-[#F8F6F0]"}>{a || "—"}</div>
      <div className={diff ? "text-amber-400" : "text-[#F8F6F0]"}>{b || "—"}</div>
      <div className="col-span-2 text-[9px] font-mono uppercase tracking-[0.15em] text-[#8E8E9A] -mt-1">{label}</div>
    </div>
  );
}

function DuplicateCard({ flag, product, candidate, onDone }) {
  const [busy, setBusy] = useState(false);

  const merge = async () => {
    setBusy(true);
    try {
      // keep the existing master (candidate), fold the new draft (product) into it
      await api.post("/admin/products/merge", {
        master_id: candidate.id,
        duplicate_id: product.id,
        flag_id: flag.id,
      });
      toast.success("Products merged into master");
      onDone();
    } catch (e) {
      toast.error("Merge failed");
    } finally {
      setBusy(false);
    }
  };

  const dismiss = async () => {
    setBusy(true);
    await api.post(`/admin/duplicates/${flag.id}/dismiss`);
    toast("Flag dismissed — kept as separate products");
    onDone();
  };

  return (
    <div className="bg-[#141417] border border-white/10 rounded-sm p-6" data-testid={`duplicate-card-${flag.id}`}>
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-2 text-amber-400">
          <ShieldAlert size={16} />
          <span className="text-xs font-mono uppercase tracking-[0.15em]">Possible Duplicate</span>
        </div>
        <span className="font-serif text-2xl text-[#D4AF37]" data-testid={`match-score-${flag.id}`}>
          {(flag.confidence * 100).toFixed(0)}% match
        </span>
      </div>

      <div className="grid grid-cols-2 gap-4 mb-4">
        {[product, candidate].map((item, idx) => (
          <div key={item.id} className="text-center">
            <div className="aspect-[3/4] rounded-sm overflow-hidden bg-[#0B0B0C] mb-2 border border-white/10">
              <img src={mediaUrl(item.hero_thumbnail)} alt="" className="w-full h-full object-cover" />
            </div>
            <p className="overline text-[9px]">{idx === 0 ? "New Draft" : "Existing Master"}</p>
            <p className="font-serif text-lg font-light">{item.title}</p>
          </div>
        ))}
      </div>

      <div className="mb-6">
        <AttrRow label="Type" a={product.product_type} b={candidate.product_type} />
        <AttrRow label="Color" a={product.color} b={candidate.color} />
        <AttrRow label="Fabric" a={product.fabric} b={candidate.fabric} />
        <AttrRow label="Occasion" a={product.occasion} b={candidate.occasion} />
      </div>

      <div className="flex gap-3">
        <button
          onClick={merge}
          disabled={busy}
          data-testid={`merge-confirm-${flag.id}`}
          className="flex-1 flex items-center justify-center gap-2 bg-[#D4AF37] text-black py-3 rounded-sm text-sm font-medium hover:bg-[#C5A059] transition-colors disabled:opacity-50"
        >
          <GitMerge size={15} /> Merge into Master
        </button>
        <button
          onClick={dismiss}
          disabled={busy}
          data-testid={`dismiss-flag-${flag.id}`}
          className="flex items-center justify-center gap-2 border border-white/15 px-5 py-3 rounded-sm text-sm hover:border-white/40 transition-colors"
        >
          <X size={15} /> Keep Separate
        </button>
      </div>
    </div>
  );
}

export default function AdminDuplicates() {
  const [flags, setFlags] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/duplicates").then((r) => {
      setFlags(r.data);
      setLoading(false);
    });
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <div className="fade-in">
      <p className="overline mb-2">Catalogue Integrity</p>
      <h1 className="font-serif text-4xl font-light mb-3">Duplicate Review</h1>
      <p className="text-[#8E8E9A] text-sm font-light mb-8 max-w-2xl">
        AI flags likely repeat sightings across films. Nothing is auto-merged — you decide whether two candidates are the same master product.
      </p>

      {loading ? (
        <div className="overline animate-pulse">Loading…</div>
      ) : flags.length === 0 ? (
        <div className="flex flex-col items-center py-24 text-center">
          <GitMerge size={36} className="text-[#8E8E9A]" />
          <p className="font-serif text-xl font-light mt-4">No open duplicate flags.</p>
          <p className="text-[#8E8E9A] text-sm mt-1">Your catalogue is clean.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {flags.map((f) => (
            <DuplicateCard key={f.flag.id} flag={f.flag} product={f.product} candidate={f.candidate} onDone={load} />
          ))}
        </div>
      )}
    </div>
  );
}
